/**
 * Global privacy-mode toggle.
 *
 * When on, every <Sensitive> wrapper renders the redacted placeholder
 * regardless of the user's own field-visibility rules. Use case: someone
 * walks past, screen-share, demo. Cmd+Shift+B toggles. Persists in
 * localStorage so a refresh doesn't flash the raw content.
 *
 * This is UI self-protection, NOT authorization. The server still
 * enforces field visibility per role; this layer just suppresses what
 * WOULD have been shown to the legitimate user in that session.
 */

import { create } from 'zustand'

const STORAGE_KEY = 'mc-erp.privacyMode'

interface PrivacyState {
  privacyMode: boolean
  toggle: () => void
  set: (value: boolean) => void
}

function readInitial(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1'
  } catch { return false }
}

function persist(value: boolean) {
  try {
    if (value) window.localStorage.setItem(STORAGE_KEY, '1')
    else window.localStorage.removeItem(STORAGE_KEY)
  } catch { /* ignore */ }
}

function applyPrivacyClass(on: boolean) {
  try {
    const root = document.documentElement
    if (on) root.setAttribute('data-privacy-mode', 'on')
    else root.removeAttribute('data-privacy-mode')
  } catch { /* no-DOM environments */ }
}

export const usePrivacyStore = create<PrivacyState>((set, get) => ({
  privacyMode: readInitial(),
  toggle: () => {
    const next = !get().privacyMode
    persist(next); applyPrivacyClass(next); set({ privacyMode: next })
  },
  set: (value: boolean) => {
    persist(value); applyPrivacyClass(value); set({ privacyMode: value })
  },
}))

// Sync the attribute on first load (persisted mode survives refresh).
if (typeof window !== 'undefined') applyPrivacyClass(readInitial())
